<template>
  <div v-if="!loading">
    <FlowHeading
      :title="$t('Add your business details')"
      :sub-title="
        $t('This information allows us to show your correct business information on your bills.')
      "
    />

    <fieldset>
      <legend>
        {{ $t('Select your business entity type') }}
      </legend>

      <NlBusinessEntityTypeRadioGroup v-model="businessEntityType" name="business_entity" />
    </fieldset>

    <fieldset v-if="hasSelectedValidBusinessEntityType" class="tw-mt-5">
      <legend>
        {{ $t('Add your business details') }}
      </legend>

      <NlBusinessEntityDetailsForm
        v-model:business-entity="internalBusinessEntity"
        :compliance-data="complianceData"
        :errors-graphql="errors"
        @update:is-vat-registered="isVatRegistered = $event"
      />
    </fieldset>

    <div class="wds-input-group tw-mt-6 tw-mb-4">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>

    <div class="tw-mt-4">
      <WdsStack justify-center>
        <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { WdsButton, WdsButtonGhost, WdsStack } from '@worksome/wds-components'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import NlBusinessEntityDetailsForm from '@/components/compliances/BusinessEntity/Nl/NlBusinessEntityDetailsForm.vue'
import NlBusinessEntityTypeRadioGroup from '@/components/compliances/BusinessEntity/Nl/NlBusinessEntityTypeRadioGroup.vue'
import { ComplianceName } from '@/components/compliances/types/ComplianceName.enum'
import businessEntityFromGraphql from '@/components/compliances/utils/graphql/businessEntityFromGraphql'
import { FormValidation } from '@/utils/formValidation'

const VALID_NL_ENTITY_TYPES = [
  'nl_limited',
  'nl_sole_trader',
  'nl_general_partnership',
  'nl_professional_partnership',
]

export default {
  name: 'NlBusinessEntityTypeDetails',

  components: {
    ButtonSpinner,
    FlowHeading,
    NlBusinessEntityDetailsForm,
    NlBusinessEntityTypeRadioGroup,
    WdsButton,
    WdsButtonGhost,
    WdsStack,
  },

  inject: ['skip', 'next'],

  setup() {
    const internalBusinessEntity = ref(businessEntityFromGraphql(null))
    const complianceData = ref({})
    const errors = ref({})
    const isVatRegistered = ref(false)

    const businessEntityType = computed({
      get() {
        return internalBusinessEntity.value.entityType
      },
      set(value) {
        internalBusinessEntity.value.entityType = value
      },
    })

    const hasSelectedValidBusinessEntityType = computed(() =>
      VALID_NL_ENTITY_TYPES.includes(businessEntityType.value),
    )

    return {
      businessEntityType,
      complianceData,
      errors,
      hasSelectedValidBusinessEntityType,
      internalBusinessEntity,
      isVatRegistered,
    }
  },

  data() {
    return {
      isSubmitting: false,
      loading: true,
    }
  },

  computed: {
    canSubmit() {
      if (this.isSubmitting) {
        return false
      }

      if (this.businessEntityType === 'no_setup') {
        return true
      }

      if (!this.hasSelectedValidBusinessEntityType) {
        return false
      }

      return (
        !!this.internalBusinessEntity.companyName &&
        !!this.internalBusinessEntity.companyNo &&
        (!this.isVatRegistered || !!this.internalBusinessEntity.taxNo)
      )
    },
  },

  apollo: {
    accountQuery: {
      query: gql`
        query nlBusinessEntitySignup($complianceNames: [ComplianceName!]!) {
          me {
            activeAccount {
              globalId
              compliances(names: $complianceNames) {
                name
                isApplicable
                isComplete
                data
              }
              ... on Freelancer {
                primaryBusinessEntity {
                  globalId
                  entityType
                  isPrimary
                  label
                  companyNo
                  companyName
                  taxNo
                  nationalIdentifier
                  data
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          complianceNames: [ComplianceName.FREELANCER_NL_BUSINESS_ENTITY],
        }
      },
      result({ data }) {
        const account = data?.me?.activeAccount
        if (!account) {
          this.loading = false
          return
        }

        this.internalBusinessEntity = businessEntityFromGraphql(account.primaryBusinessEntity)
        this.isVatRegistered =
          this.internalBusinessEntity.taxNo !== null && this.internalBusinessEntity.taxNo !== ''

        const compliance = (account.compliances ?? []).find(
          (c) => c.name === ComplianceName.FREELANCER_NL_BUSINESS_ENTITY,
        )
        if (compliance?.data) {
          this.complianceData =
            typeof compliance.data === 'string' ? JSON.parse(compliance.data) : compliance.data
        }

        this.loading = false
      },
      update: (data) => data.me,
    },
  },

  methods: {
    async callNextScreen() {
      if (!this.canSubmit) {
        return
      }

      this.errors = {}
      this.isSubmitting = true

      await this.updateBusinessEntityDetails()

      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },

    async updateBusinessEntityDetails() {
      const isNoSetup = this.businessEntityType === 'no_setup'

      await this.$apollo
        .mutate({
          mutation: gql`
            mutation updateCompliance($input: UpdateComplianceInput!) {
              updateCompliance(input: $input) {
                name
                isApplicable
                isComplete
                data
              }
            }
          `,
          variables: {
            input: {
              name: ComplianceName.FREELANCER_NL_BUSINESS_ENTITY,
              data: JSON.stringify({
                business_entity: this.businessEntityType,
                registered_company_name: isNoSetup
                  ? ''
                  : (this.internalBusinessEntity.companyName ?? ''),
                company_no: isNoSetup ? '' : (this.internalBusinessEntity.companyNo ?? ''),
                is_vat_registered: !isNoSetup && this.isVatRegistered,
                vat_no:
                  !isNoSetup && this.isVatRegistered ? this.internalBusinessEntity.taxNo : null,
                business_entity_data: isNoSetup ? {} : (this.internalBusinessEntity.data ?? {}),
              }),
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
          refetchQueries: ['nlBusinessEntitySignup'],
          awaitRefetchQueries: true,
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },
  },
}
</script>
